import { useMemo, useState } from 'react';
import { initialValues, lookupKeys, parseParamsSchema, rangeKeys, toParamLabels, toParams, type Errors, type Field, type Values } from '../lib/schemaForm.ts';
import { T } from '../lib/strings.ts';
import { Input, Label, Select } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { IconPlay } from './brand/icons.tsx';
import { LookupField, type LookupFn } from './LookupField.tsx';

const MSG = {
  required: T.form.required,
  number: T.form.number,
  integer: T.form.integer,
  min: T.form.min,
  max: T.form.max,
  date: T.form.date,
  range: T.form.range,
  minLen: (n: number) => T.form.invalidLen(n),
  maxLen: (n: number) => T.form.invalidLen(undefined, n),
  choice: T.form.required,
  pick: T.form.pick,
};

function FieldView({ f, values, set, error, lookup }: { f: Field; values: Values; set: (k: string, v: string) => void; error?: string; lookup?: LookupFn }) {
  const id = `p-${f.id}`;
  const err = error ? `${id}-err` : undefined;
  const common = { 'aria-invalid': error ? true : undefined, 'aria-describedby': err };
  const req = f.required ? <span aria-hidden="true" className="text-error"> *</span> : null;
  let control: React.ReactNode;
  switch (f.kind) {
    case 'date-range': {
      const k = rangeKeys(f);
      return (
        <fieldset className="flex flex-col gap-1 sm:col-span-2" data-field={f.id} data-kind="date-range">
          <legend className="mb-1 text-sm font-semibold text-foreground/90">
            {f.label}
            {req}
          </legend>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-from`} className="text-muted-foreground">
                {T.form.from}
              </Label>
              <Input id={`${id}-from`} name={k.from} type="date" value={values[k.from] ?? ''} onChange={(e) => set(k.from, e.target.value)} required={f.required} {...common} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-to`} className="text-muted-foreground">
                {T.form.to}
              </Label>
              <Input id={`${id}-to`} name={k.to} type="date" value={values[k.to] ?? ''} onChange={(e) => set(k.to, e.target.value)} required={f.required} {...common} />
            </div>
          </div>
          {error && (
            <p id={err} className="m-0 text-sm text-error">
              {error}
            </p>
          )}
        </fieldset>
      );
    }
    case 'enum':
      control = (
        <Select id={id} name={f.id} value={values[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)} required={f.required} {...common}>
          {(!f.required || !f.default) && <option value="">{T.form.choose}</option>}
          {f.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
      break;
    case 'number':
      control = (
        <Input id={id} name={f.id} type="text" inputMode="decimal" dir="ltr" className="text-end" value={values[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)} {...common} />
      );
      break;
    case 'date':
      control = <Input id={id} name={f.id} type="date" value={values[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)} {...common} />;
      break;
    case 'lookup':
      // artifacts-report-lookup: בלי פונקציית חיפוש (תצוגה בלי יצירה) — טקסט רגיל, והשרת יחליט
      control = lookup ? (
        <LookupField f={f} id={id} values={values} set={set} lookup={lookup} invalid={!!error} describedBy={err} />
      ) : (
        <Input id={id} name={f.id} type="text" dir="auto" value={values[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)} {...common} />
      );
      break;
    case 'string':
      control = <Input id={id} name={f.id} type="text" dir="auto" value={values[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)} maxLength={f.maxLength} {...common} />;
      break;
    default:
      control = <p className="m-0 text-sm text-muted-foreground">{T.form.unsupported}</p>;
  }
  return (
    <div className="flex flex-col gap-1" data-field={f.id} data-kind={f.kind}>
      <Label htmlFor={id}>
        {f.label}
        {req}
      </Label>
      {control}
      {error && (
        <p id={err} className="m-0 text-sm text-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * submit: כפתור אחר מ"הרצה" (19: "שמירת תזמון" — אותו טופס פרמטרים). lookup (report@3): החיפוש של שדות בחירה עם חיפוש;
 * onSubmit מקבל גם paramLabels — התווית של מה שנבחר (undefined כשאין).
 */
export function ParamsForm({
  schema, busy, onSubmit, initial, submit, lookup,
}: {
  schema: unknown;
  busy: boolean;
  onSubmit: (params: Record<string, unknown>, values: Values, paramLabels?: Record<string, string>) => void;
  initial?: Values;
  submit?: { label: string; busyLabel: string; testId: string };
  lookup?: LookupFn;
}) {
  const fields = useMemo(() => parseParamsSchema(schema), [schema]);
  const [values, setValues] = useState<Values>(() => ({ ...initialValues(fields), ...initial }));
  const [errors, setErrors] = useState<Errors>({});
  const set = (k: string, v: string) => setValues((s) => ({ ...s, [k]: v }));
  return (
    <form
      noValidate
      aria-label={T.artifact.params}
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        const r = toParams(fields, values, MSG);
        if (!r.ok) return setErrors(r.errors);
        setErrors({});
        onSubmit(r.params, values, toParamLabels(fields, values));
      }}
    >
      {fields.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {fields.map((f) => (
            <FieldView key={f.id} f={f} values={values} set={set} error={errors[f.id]} lookup={lookup} />
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 border-t border-border pt-4">
        <Button type="submit" size="lg" disabled={busy} className="w-full sm:w-auto sm:min-w-36" data-testid={submit?.testId ?? 'run-btn'}>
          {busy ? (
            <span aria-hidden="true" className="size-4 animate-spin rounded-circle border-2 border-current border-e-transparent" />
          ) : submit ? null : (
            <IconPlay size={16} className="fill-current" />
          )}
          {busy ? (submit?.busyLabel ?? T.artifact.running) : (submit?.label ?? T.artifact.run)}
        </Button>
      </div>
    </form>
  );
}

/** תקציר הפרמטרים אחרי הרצה (מובייל: הטופס מתקפל). בחירה עם חיפוש — התווית של מה שנבחר. */
export function paramsSummary(schema: unknown, values: Values): string {
  return parseParamsSchema(schema)
    .flatMap((f) => {
      if (f.kind === 'date-range') {
        const k = rangeKeys(f);
        return values[k.from] ? [`${f.label}: ${values[k.from]} – ${values[k.to]}`] : [];
      }
      const v = values[f.id];
      if (!v) return [];
      const shown = f.kind === 'enum' ? (f.options.find((o) => o.value === v)?.label ?? v) : f.kind === 'lookup' ? values[lookupKeys(f).label] || values[lookupKeys(f).value] || v : v;
      return [`${f.label}: ${shown}`];
    })
    .join(' · ');
}
